import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { Custody } from '@infinex/types';

import type {
  CustodyWithBalanceForList,
  ExternalWalletWithBalanceForList,
  VaultWithBalanceForList,
  WalletWithBalanceForList,
} from '../../api/user/hooks/useCustodiesListWithBalance';

export type CustodySelected =
  | { custody: 'vault' }
  | { custody: 'wallet'; id: string }
  | { custody: 'external-signer'; id: string };

type CustodyContextType = {
  custodiesWithBalance: CustodyWithBalanceForList[] | undefined;
  isLoading: boolean;

  selected:
    | VaultWithBalanceForList
    | WalletWithBalanceForList
    | ExternalWalletWithBalanceForList
    | undefined;
  setSelected: (custody: CustodySelected) => void;
  setDefaultSelected: (selected: CustodySelected | undefined) => void;
};

export const CustodySelectContext = createContext<
  CustodyContextType | undefined
>(undefined);

function findCustody(
  selected: CustodySelected | undefined,
  custodiesWithBalance?: CustodyWithBalanceForList[]
):
  | VaultWithBalanceForList
  | WalletWithBalanceForList
  | ExternalWalletWithBalanceForList
  | undefined {
  if (!custodiesWithBalance) return undefined;
  if (!selected) {
    // find first custody
    const defaultCustody = custodiesWithBalance.at(0);
    if (defaultCustody) {
      if (defaultCustody.groupKind === 'vault') {
        return defaultCustody;
      }
      // Recovery phrases and hardware wallets have .wallets array
      // Private keys have .wallet (singular)
      if (
        defaultCustody.groupKind === 'recoveryPhrase' ||
        defaultCustody.groupKind === 'externalSigningDevice'
      ) {
        const firstWallet = defaultCustody.wallets.at(0);
        if (firstWallet) {
          return firstWallet;
        }
      } else if (defaultCustody.groupKind === 'privateKey') {
        return defaultCustody.wallet;
      }
    }
  }

  if (selected?.custody === 'vault') {
    return custodiesWithBalance.find((c) => c.groupKind === 'vault');
  }

  const walletId = selected?.id;
  for (const custody of custodiesWithBalance) {
    if (custody.groupKind === 'privateKey') {
      if (custody.wallet.id === walletId) {
        return custody.wallet;
      }
    }

    if (
      custody.groupKind === 'recoveryPhrase' ||
      custody.groupKind === 'externalSigningDevice'
    ) {
      for (const wallet of custody.wallets) {
        const matching = wallet.id === walletId;
        if (matching) {
          return wallet;
        }
      }
    }
  }
}

export const CustodySelectionProvider = ({
  children,
  custodiesWithBalance,
  isLoading,
  shouldAutoSelectFirst = true,
}: {
  children: React.ReactNode;
  custodiesWithBalance?: CustodyWithBalanceForList[];
  isLoading: boolean;
  shouldAutoSelectFirst?: boolean;
}) => {
  const [selected, setSelected] = useState<
    | VaultWithBalanceForList
    | WalletWithBalanceForList
    | ExternalWalletWithBalanceForList
    | undefined
  >(undefined);

  const [hasManuallySetSelection, setHasManuallySetSelection] = useState(false);

  const setDefaultSelected = useCallback(
    (custodySelected: CustodySelected | undefined) => {
      if (selected && hasManuallySetSelection) return;
      const custody = findCustody(custodySelected, custodiesWithBalance);
      setSelected(custody);
    },
    [selected, custodiesWithBalance, hasManuallySetSelection]
  );

  useEffect(() => {
    if (!shouldAutoSelectFirst) return;
    setDefaultSelected(undefined /* select first */);
  }, [setDefaultSelected, shouldAutoSelectFirst]);

  const handleSelect = (custody: CustodySelected) => {
    const found = findCustody(custody, custodiesWithBalance);
    setSelected(found);
    setHasManuallySetSelection(true);
  };

  return (
    <CustodySelectContext.Provider
      value={{
        custodiesWithBalance,
        isLoading,
        selected,
        setSelected: handleSelect,
        setDefaultSelected,
      }}
    >
      {children}
    </CustodySelectContext.Provider>
  );
};

export function useCustodySelection() {
  const context = useContext(CustodySelectContext);
  if (!context)
    throw new Error('useWallet must be used within a WalletProvider');
  return context;
}

export const useUpdateDefaultSelectedGasCustody = (
  custody?: Custody,
  walletId?: string
) => {
  const { setDefaultSelected } = useCustodySelection();
  useEffect(() => {
    if (custody === 'wallet' && walletId) {
      setDefaultSelected({ custody: 'wallet', id: walletId });
    } else if (custody === 'vault') {
      setDefaultSelected({ custody: 'vault' });
    }
  }, [custody, walletId, setDefaultSelected]);
};
